import moment from 'moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { deserialize } from '../../../../../utility/serialization'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../../utility/datesHelper'
import { requestCommunityProgramsEnrollmentsActivityReport } from '../../../../../actions/reportsActions'
import Loader from '../../../../common/loader/Loader'
import Select from '../../../../common/form/Select'

import { buildCommunityYearRangeFunctions } from '../../../../../utility/communityYearRanges'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './ProgramsEnrollmentsActivityWidget.module.scss'
import ProgramsEnrollmentsLineChart from './ProgramsEnrollmentsLineChart'
import Placeholder, { EXCEPTION_CASE } from './Placeholder'

const SINGLE_STATISTICS_COLORS = {
  blue: 'blue',
}

const SingleStatistics = ({ data, topLabel, bottomLabel, bottomLabelColor, hasZeroInsteadOfDash, withGrowthLabel }) => {
  const { t } = useTranslation()
  const growthValue = data.prevValue ? data.value - data.prevValue : null
  const useDash = data.value === null || data.value === 0 && !hasZeroInsteadOfDash

  return (
    <div className={classnames('d-flex flex-column text-center', businessDashboardClasses.statistics)}>
      <span className={businessDashboardClasses.topLabel}>{topLabel}</span>
      <span className={businessDashboardClasses.value}>
        {useDash ? '-' : data.value}
      </span>
      {withGrowthLabel ? (
        <span
          className={
            classnames(businessDashboardClasses.bottomLabel, growthValue < 0 ? businessDashboardClasses.red : businessDashboardClasses.green)
          }
        >
          {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.growthVsLast30Days', {
            value: Intl.NumberFormat('en-US', { signDisplay: 'always' })
              .format(growthValue),
          })}
        </span>
      ) : (
        <span className={classnames(businessDashboardClasses.bottomLabel, { [businessDashboardClasses[bottomLabelColor]]: !!bottomLabelColor })}>
          {bottomLabel}
        </span>
      )}
    </div>
  )
}

SingleStatistics.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.number,
    prevValue: PropTypes.number,
  }).isRequired,
  topLabel: PropTypes.string.isRequired,
  bottomLabel: PropTypes.string,
  bottomLabelColor: PropTypes.string,
  hasZeroInsteadOfDash: PropTypes.bool,
  withGrowthLabel: PropTypes.bool,
}

const ProgramsEnrollmentsActivityWidget = () => {
  const { t } = useTranslation()
  const community = useSelector(state => state.user.current_user.communities[0])
  const communityYearRangeFunctions = useMemo(() => buildCommunityYearRangeFunctions(community?.created_at), [community?.created_at])
  const yearSelectOptions = useMemo(() => Object.keys(communityYearRangeFunctions).reverse()
    .map(key => {
      const { tKey, values } = communityYearRangeFunctions[key].textFunc()
      return { label: t(tKey, values), value: key }
    }), [communityYearRangeFunctions, t])
  const defaultFilters = useMemo(() => ({
    yearOption: yearSelectOptions[0],
  }), [yearSelectOptions])

  const [filters, setFilters] = useState(defaultFilters)
  const [isLoadingChartData, setIsLoadingChartData] = useState(true)
  const [data, setData] = useState()
  const exceptionCase = useMemo(() => {
    if (data?.selectedRange?.statistics?.totalCourses.value === 0) {
      return EXCEPTION_CASE.NO_PROGRAMS
    } else if (data?.selectedRange?.monthlyData?.every(month => month.value === 0)) {
      return EXCEPTION_CASE.NO_ENROLLMENTS
    } else {
      return null
    }
  }, [data?.selectedRange?.monthlyData, data?.selectedRange?.statistics?.totalCourses.value])

  useEffect(() => {
    setIsLoadingChartData(true)

    const { startDate, endDate } = communityYearRangeFunctions[filters.yearOption.value].valueFunc()

    requestCommunityProgramsEnrollmentsActivityReport({
      communityId: community?.id,
      startDate: getMomentInCommunityTimezone(moment(startDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
      endDate: getMomentInCommunityTimezone(moment(endDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoadingChartData(false)
      })
      .catch(() => toast.error(t(COMMON_ERRORS[500])))
  }, [community, communityYearRangeFunctions, filters.yearOption])

  const handleSelectYearOption = (yearOption) => {
    setFilters(prevState => ({ ...prevState, yearOption }))
  }

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={classnames('d-flex justify-content-between', businessDashboardClasses.titleWrapper)}>
        <h2 className={businessDashboardClasses.title}>
          {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.title')}
        </h2>
        <Select
          id='programsEnrollmentsYearSelect'
          classNames={{
            control: () => classes.yearSelectControl,
            singleValue: () => classes.yearSelectSingleValue,
            menuList: () => classes.yearSelectMenuList,
          }}
          options={yearSelectOptions}
          value={filters.yearOption}
          onChange={handleSelectYearOption}
          isSearchable={false}
        />
      </div>
      {isLoadingChartData ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>{t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.loadingData')}</Loader>
        </div>
      ) : (
        <>
          <div className='d-flex'>
            <div style={{ flexBasis: '55%' }} className='mr-4'>
              <h3 className={businessDashboardClasses.statisticsTitle}>
                {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.todaysActivityTitle')}
              </h3>
              <span className={businessDashboardClasses.statisticsSubtitle}>
                {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.todaysActivitySubtitle')}
              </span>
              <div className={classnames('d-flex justify-content-between', businessDashboardClasses.sectionWrapper)}>
                <SingleStatistics
                  topLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.activePrograms')}
                  data={data.currentTime.statistics.runningCourses}
                  withGrowthLabel
                />
                <SingleStatistics
                  data={data.currentTime.statistics.runningEnrollments}
                  topLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.activeEnrollments')}
                  withGrowthLabel
                />
                <SingleStatistics
                  data={data.currentTime.statistics.spotsAvailable}
                  topLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.spotsAvailable')}
                  hasZeroInsteadOfDash
                  bottomLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.totalCapacity')}
                  bottomLabelColor={SINGLE_STATISTICS_COLORS.blue}
                />
                <SingleStatistics
                  data={data.currentTime.statistics.waitlistSize}
                  topLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.waitlisted')}
                  bottomLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.golfersOnWaitlist')}
                  bottomLabelColor={SINGLE_STATISTICS_COLORS.blue}
                />
              </div>
            </div>
            <div className='flex-grow-1'>
              <h3 className={businessDashboardClasses.statisticsTitle}>
                {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.performanceTrendsTitle')}
              </h3>
              <span className={businessDashboardClasses.statisticsSubtitle}>
                {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.performanceTrendsSubtitle')}
              </span>
              <div className={classnames('d-flex justify-content-between', businessDashboardClasses.sectionWrapper)}>
                <SingleStatistics
                  data={data.selectedRange.statistics.totalCourses}
                  topLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.totalPrograms')}
                  bottomLabel={
                    t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.inYear', { year: filters.yearOption.value })
                  }
                />
                <SingleStatistics
                  data={data.selectedRange.statistics.completedCourses}
                  topLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.completedPrograms')}
                  bottomLabel={
                    t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.inYear', { year: filters.yearOption.value })
                  }
                />
                <SingleStatistics
                  data={data.selectedRange.statistics.totalEnrollments}
                  topLabel={t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.totalEnrollments')}
                  bottomLabel={
                    t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsActivityWidget.inYear', { year: filters.yearOption.value })
                  }
                />
              </div>
            </div>
          </div>
          <div className={businessDashboardClasses.chartWrapper}>
            {exceptionCase ? (
              <Placeholder exceptionCase={exceptionCase} />
            ) : (
              <ProgramsEnrollmentsLineChart monthlyData={data.selectedRange.monthlyData} />
            )}
          </div>

        </>
      )}
    </section>
  )
}

export default ProgramsEnrollmentsActivityWidget
